import { useState } from 'react';
import { Link } from 'react-router';
import { Confirm, EmptyState, ErrorState, Spinner, StatusBadge } from '../../components/ui.jsx';
import { api } from '../../api/client.js';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { useMeta } from '../../state/MetaContext.jsx';
import { useToast } from '../../state/ToastContext.jsx';
import { formatDate, formatXaf, timeAgo } from '../../lib/format.js';

const STAGES = ['submitted', 'under_review', 'approved'];

// A simple stepper so a tenant can see how far along an application is without
// reading the status jargon.
const Progress = ({ status }) => {
  if (status === 'rejected' || status === 'withdrawn') {
    return (
      <p className="progress-closed">
        {status === 'rejected' ? 'Closed: not successful' : 'Closed: you withdrew this application'}
      </p>
    );
  }

  const reached = STAGES.indexOf(status);
  return (
    <ol className="progress">
      {['Submitted', 'Under review', 'Approved'].map((label, index) => (
        <li key={label} className={index <= reached ? 'is-done' : ''}>
          <span className="progress-dot" aria-hidden="true" />
          {label}
        </li>
      ))}
    </ol>
  );
};

const DocumentUploader = ({ application, onUploaded }) => {
  const { documentKinds } = useMeta();
  const toast = useToast();
  const [kind, setKind] = useState('id_card');
  const [busy, setBusy] = useState(false);

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const formData = new FormData();
      formData.append('kind', kind);
      for (const file of files) formData.append('files', file);
      await api.upload(`/applications/${application.id}/documents`, formData);
      toast.success('Document added.');
      onUploaded();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="doc-uploader">
      <select value={kind} onChange={(event) => setKind(event.target.value)} disabled={busy}>
        {documentKinds.map((item) => (
          <option key={item.slug} value={item.slug}>
            {item.label}
          </option>
        ))}
      </select>
      <label className="btn btn-outline btn-sm">
        {busy ? 'Uploading...' : 'Add document'}
        <input
          type="file"
          multiple
          hidden
          accept="image/*,application/pdf"
          disabled={busy}
          onChange={(event) => upload(event.target.files)}
        />
      </label>
    </div>
  );
};

const TenantApplications = () => {
  useTitle('My applications');
  const toast = useToast();
  const { data, loading, error, reload } = useFetch('/applications/mine');
  const [filter, setFilter] = useState('all');
  const [withdrawing, setWithdrawing] = useState(null);
  const [busy, setBusy] = useState(false);

  const items = data?.items || [];
  const visible =
    filter === 'all'
      ? items
      : filter === 'open'
        ? items.filter((item) => ['submitted', 'under_review'].includes(item.status))
        : items.filter((item) => item.status === filter);

  const withdraw = async () => {
    setBusy(true);
    try {
      await api.post(`/applications/${withdrawing.id}/withdraw`);
      toast.success('Application withdrawn.');
      setWithdrawing(null);
      reload();
    } catch (requestError) {
      toast.error(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading && !data) return <Spinner />;
  if (error) {
    return (
      <div className="container section">
        <ErrorState message={error} onRetry={reload} />
      </div>
    );
  }

  return (
    <div className="container section">
      <header className="page-head">
        <div>
          <h1>My applications</h1>
          <p className="muted">Every house you have applied for and where each one stands.</p>
        </div>
      </header>

      <div className="tab-row">
        {[
          ['all', 'All'],
          ['open', 'Waiting'],
          ['approved', 'Approved'],
          ['rejected', 'Not successful'],
          ['withdrawn', 'Withdrawn']
        ].map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={`tab ${filter === value ? 'is-current' : ''}`}
            onClick={() => setFilter(value)}
          >
            {label}
            {value === 'all' ? ` (${items.length})` : ''}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <EmptyState
          title="Nothing in this list"
          message={filter === 'all' ? 'You have not applied for any house yet.' : 'Try another tab.'}
          action={
            filter === 'all' ? (
              <Link to="/search" className="btn btn-primary">
                Find a house
              </Link>
            ) : null
          }
        />
      ) : null}

      <div className="application-list">
        {visible.map((application) => (
          <article key={application.id} className="application-card">
            <Link to={`/properties/${application.propertyId}`} className="application-media">
              {application.property?.coverImage ? (
                <img src={application.property.coverImage} alt="" />
              ) : (
                <span className="property-card-noimage">No photo</span>
              )}
            </Link>

            <div className="application-body">
              <div className="application-head">
                <div>
                  <h2>
                    <Link to={`/properties/${application.propertyId}`}>
                      {application.property?.title || 'Listing removed'}
                    </Link>
                  </h2>
                  <p className="muted">
                    {application.property
                      ? `${[application.property.neighbourhood, application.property.city].filter(Boolean).join(', ')} - ${formatXaf(application.property.price)} / month`
                      : ''}
                  </p>
                </div>
                <StatusBadge status={application.status} />
              </div>

              <Progress status={application.status} />

              <dl className="application-meta">
                <div>
                  <dt>Applied</dt>
                  <dd>{timeAgo(application.createdAt)}</dd>
                </div>
                <div>
                  <dt>People</dt>
                  <dd>{application.occupants}</dd>
                </div>
                {application.moveInDate ? (
                  <div>
                    <dt>Move in</dt>
                    <dd>{formatDate(application.moveInDate)}</dd>
                  </div>
                ) : null}
                {application.monthlyIncome ? (
                  <div>
                    <dt>Income given</dt>
                    <dd>{formatXaf(application.monthlyIncome)}</dd>
                  </div>
                ) : null}
              </dl>

              {application.decisionNote ? (
                <p className={`decision-note ${application.status === 'rejected' ? 'is-bad' : 'is-good'}`}>
                  <strong>From the landlord:</strong> {application.decisionNote}
                </p>
              ) : null}

              {application.documents?.length ? (
                <ul className="doc-list">
                  {application.documents.map((document) => (
                    <li key={document.id}>
                      <a href={document.url} target="_blank" rel="noreferrer noopener">
                        {document.label || document.kind}
                      </a>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="field-hint">No documents attached. Landlords usually ask for an ID and proof of income.</p>
              )}

              <div className="row-actions">
                {['submitted', 'under_review', 'approved'].includes(application.status) ? (
                  <DocumentUploader application={application} onUploaded={reload} />
                ) : null}

                {application.property ? (
                  <Link
                    to={`/messages?propertyId=${application.propertyId}&withUser=${application.property.landlordId}`}
                    className="btn btn-ghost btn-sm"
                  >
                    Message the landlord
                  </Link>
                ) : null}

                {['submitted', 'under_review'].includes(application.status) ? (
                  <button type="button" className="link-btn danger-link" onClick={() => setWithdrawing(application)}>
                    Withdraw
                  </button>
                ) : null}
              </div>
            </div>
          </article>
        ))}
      </div>

      {withdrawing ? (
        <Confirm
          title="Withdraw this application?"
          message={`The landlord will be told you are no longer interested in "${withdrawing.property?.title}". You can apply again later if you change your mind.`}
          confirmLabel="Withdraw it"
          busy={busy}
          onConfirm={withdraw}
          onClose={() => setWithdrawing(null)}
        />
      ) : null}
    </div>
  );
};

export default TenantApplications;
